import {
  COOKIE_YM_UID,
  getCookie,
  getEcommerceCookieData,
  setEcommerceCookieData,
} from '@utils/cookies';
import {getEnvConfig} from '@web-catalog/env';
import {nanoid} from 'nanoid';

import {FAILED_AUTH_INDEX} from '@web-catalog/const/numbers';

import {
  AppStub,
  AppType,
  AppWithDeviceSpecifics,
  EnvConfig,
} from '@web-catalog/types';

import {getOid, getOidSegments} from '@utils/omicron/getOidSegments';

import {
  CustomEventProps,
  EcommerceEventParams,
  LogTmrEvent,
  LogTmrEventBaseParams,
} from './logEvents.entity';

let vkId = FAILED_AUTH_INDEX;
let appInfo: AppWithDeviceSpecifics | AppStub | undefined;

export const setVkIdForAnalytics = (vkIdExt: number) => (vkId = vkIdExt);
export const setAppInfoAnalytics = (
  appInfoExt?: AppWithDeviceSpecifics | AppStub,
) => (appInfo = appInfoExt);

const getProduct = () => {
  if (!(appInfo as AppWithDeviceSpecifics)?.deviceSpecifics) {
    return null;
  }

  const {TV, MOBILE} =
    (appInfo as AppWithDeviceSpecifics)?.deviceSpecifics || {};

  if (TV && !MOBILE) {
    return 'tv';
  }

  if (MOBILE) {
    return 'android';
  }

  return null;
};

const tryGetEnvConfig = (cb: (config: EnvConfig) => void) => {
  try {
    const envConfig = getEnvConfig();
    cb(envConfig);
  } catch {
    // конфига нет при global-error (как и скриптов аналитики)
    // поэтому без дефолтов
  }
};

const previousEvent: Pick<LogTmrEventBaseParams, 'event_name' | 'screen_name'> =
  {
    event_name: undefined,
    screen_name: undefined,
  };

let tmrDebug = process.env.NODE_ENV === 'development';

export const enableTmrDebugging = () => {
  tmrDebug = true;
};

export const logTmrEvent = ({
  logEvent,
  params = {},
  type = 'reachGoal',
}: LogTmrEvent) => {
  if (!window._tmr) {
    window._tmr = [];
  }

  const currentDate = new Date();

  const segments = getOidSegments();
  const userOid = getOid();

  const ymUid = getCookie(COOKIE_YM_UID);

  const baseParams: LogTmrEventBaseParams = {
    id: nanoid(),
    date: formatDate(currentDate),
    time: formatTime(currentDate),
    event_name: logEvent,
    fingerprint: ymUid,
    kid_mode: null,
    previous_event: previousEvent.event_name,
    previous_screen: previousEvent.screen_name,
    user_id: userOid,
    product: getProduct(),
    screen_name: (document.location.href.split('?')[1] ?? null)?.substring(
      0,
      255,
    ),
    session_id: null,
    tab_name: null,
    type: null,
    pageURL: document.location.href.split('?')[0],
    vk_id: vkId,
    ...segments,
  };

  tryGetEnvConfig(({ANALYTICS_ID}) => {
    if (tmrDebug) {
      // eslint-disable-next-line no-console
      console.log(
        `TMR: log event = ${logEvent}, params = ${JSON.stringify({
          ...baseParams,
          ...params,
        })}`,
      );
    }

    window._tmr.push({
      id: ANALYTICS_ID,
      type,
      goal: logEvent,
      params: {
        ...baseParams,
        ...params,
      },
    });
  });

  previousEvent.event_name = baseParams.event_name;
  previousEvent.screen_name = baseParams.screen_name;
};

export const emitTmrEvent = ({eventName, params = {}}: CustomEventProps) => {
  if (!window._tmr) {
    return;
  }

  const currentDate = new Date();

  const baseParams = {
    id: nanoid(),
    product: 'UserProfile',
    event_timestamp: `${formatDate(currentDate)} ${formatTime(currentDate)}`,
  };

  tryGetEnvConfig(({ANALYTICS_ID}) => {
    if (tmrDebug) {
      // eslint-disable-next-line no-console
      console.log(
        `TMR: emit event = ${eventName}, params = ${JSON.stringify({
          ...baseParams,
          ...params,
        })}`,
      );
    }

    window._tmr.push({
      id: ANALYTICS_ID,
      type: 'reachGoal',
      goal: eventName,
      params: {
        ...baseParams,
        ...params,
      },
    });
  });
};

export const emitYmEvent = ({eventName, params = {}}: CustomEventProps) => {
  const currentDate = new Date();
  if (!window.ym) {
    return false;
  }
  tryGetEnvConfig(({YA_METRIKA_ID}) => {
    window.ym(YA_METRIKA_ID, 'reachGoal', eventName, {
      product: 'UserProfile',
      event_timestamp: `${formatDate(currentDate)} ${formatTime(currentDate)}`,
      ...params,
    });
  });
};

const appTypeToEcommerceCategory: Record<AppType, string> = {
  [AppType.GAMES]: 'Игры',
  [AppType.MAIN]: 'Приложения',
};

export const emitEcommerceEvent = (params: EcommerceEventParams) => {
  const {eventName, app} = params;
  window.dataLayer = window.dataLayer || [];
  let listName: string | null = null;
  let listPosition: number | null = null;

  if (eventName === 'select_item') {
    listName = params.placement.listName;
    listPosition = params.placement.position + 1;

    tryGetEnvConfig(({COOKIE_DOMAIN}) => {
      setEcommerceCookieData(
        {
          packageName: app.packageName,
          listPosition: listPosition || 0,
          listName: listName || '',
        },
        COOKIE_DOMAIN,
      );
    });
  } else {
    const storedData = getEcommerceCookieData();

    if (storedData && storedData.packageName === app.packageName) {
      listName = storedData.listName;
      listPosition = storedData.listPosition;
    }
  }

  window.dataLayer.push({
    event: eventName,
    ecommerce: {
      items: [
        {
          item_name: app.packageName,
          item_id: app.appId ?? null,
          price: app.price ?? null,
          item_brand: app.developer ?? null,
          item_category: app.type ? appTypeToEcommerceCategory[app.type] : null,
          item_category_2: app.category ?? null,
          item_list_name: listName ?? null,
          index: listPosition ?? null,
          quantity: 1,
        },
      ],
    },
  });
};

const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const day = ('0' + d.getDate()).slice(-2);

  return `${year}-${month}-${day}`;
};

const formatTime = (d: Date) => {
  const hours = ('0' + d.getHours()).slice(-2);
  const minutes = ('0' + d.getMinutes()).slice(-2);
  const seconds = ('0' + d.getSeconds()).slice(-2);

  return `${hours}:${minutes}:${seconds}`;
};
